/**
 * useOtpFlow — Email verification via OTP for signup.
 *
 * Extracted from SignupContainer to reduce FTA complexity.
 * Manages the email → OTP → verified flow with auto-focus,
 * paste support, retry on cold starts, and error handling.
 */

import { useState, useRef } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { useSendOtpMutation, useVerifyOtpMutation } from '@/api/features/auth';
import type { ApiError } from '@/features/onboarding/types';
import { extractErrorMessage, extractErrorCode } from '@/features/onboarding/types';

type SignupStep = 'email' | 'otp' | 'details';

export function useOtpFlow() {
  const [signupStep, setSignupStep] = useState<SignupStep>('email');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [otpMethodId, setOtpMethodId] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();

  const handleSendOtp = async () => {
    setEmailError(null);
    setOtpError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailInput.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!emailRegex.test(emailInput.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const normalizedEmail = emailInput.trim().toLowerCase();

    // Retry logic for cold starts
    const sendWithRetry = async (attempt: number = 1): Promise<{ methodId: string }> => {
      try {
        return await sendOtp({ email: normalizedEmail }).unwrap();
      } catch (err) {
        const error = err as ApiError;
        const errorData = error?.data;

        if (extractErrorCode(errorData) === 'EMAIL_EXISTS' || error?.status === 400 || error?.status === 429) {
          throw err;
        }

        if (attempt < 2) {
          reduxLogger.warn(`[Signup] OTP send failed (attempt ${attempt}), retrying in 500ms...`);
          await new Promise(resolve => setTimeout(resolve, 500));
          return sendWithRetry(attempt + 1);
        }

        throw err;
      }
    };

    try {
      const result = await sendWithRetry();
      setVerifiedEmail(normalizedEmail);
      setOtpMethodId(result.methodId);
      setSignupStep('otp');
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch (err) {
      const error = err as ApiError;
      const errorData = error?.data;
      const errorMessage = extractErrorMessage(errorData, 'Failed to send verification code');

      if (extractErrorCode(errorData) === 'EMAIL_EXISTS') {
        setEmailError('An account with this email already exists.');
      } else {
        setEmailError(errorMessage);
      }
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setOtpError(null);

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpCode(newOtp);
      otpInputRefs.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError(null);
    const code = otpCode.join('');

    if (code.length !== 6) {
      setOtpError('Please enter the complete 6-digit code');
      return;
    }

    try {
      await verifyOtp({
        email: verifiedEmail,
        code: code,
        methodId: otpMethodId,
      }).unwrap();

      setSignupStep('details');
    } catch (err) {
      const error = err as ApiError;
      const errorData = error?.data;
      const errorMessage = extractErrorMessage(errorData, 'Invalid verification code');
      setOtpError(errorMessage);
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  const handleResendOtp = async () => {
    setOtpError(null);
    setOtpCode(['', '', '', '', '', '']);

    try {
      const result = await sendOtp({ email: verifiedEmail }).unwrap();
      setOtpMethodId(result.methodId);
      otpInputRefs.current[0]?.focus();
    } catch (err) {
      const error = err as ApiError;
      const errorData = error?.data;
      setOtpError(extractErrorMessage(errorData, 'Failed to resend code. Please try again.'));
    }
  };

  const handleBackToEmail = () => {
    setSignupStep('email');
    setOtpCode(['', '', '', '', '', '']);
    setOtpError(null);
  };

  return {
    // Step state
    signupStep,
    verifiedEmail,

    // Email step
    emailInput,
    setEmailInput,
    emailError,
    handleSendOtp,
    isSendingOtp,

    // OTP step
    otpCode,
    otpError,
    otpInputRefs,
    handleOtpChange,
    handleOtpPaste,
    handleOtpKeyDown,
    handleVerifyOtp,
    handleResendOtp,
    handleBackToEmail,
    isVerifyingOtp,
  };
}
